import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const legalLinks = [
    { label: 'Terms of Use', href: '/terms' },
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Advertising & Cookies', href: '/cookies' },
  ];

  const socialLinks = [
    { icon: Linkedin, href: '/404', label: 'LinkedIn' },
    { icon: Mail, href: 'mailto:info@brandcontentflow.com', label: 'Email' },
  ];

  return (
    <footer className="py-[35px] md:py-12 bg-slate-50 border-t border-slate-200" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* 4-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-12 mb-8">
          {/* Column 1: Logo & Description */}
          <div>
            <Link to="/" className="text-lg font-bold text-slate-800 inline-block mb-3">
              BrandContent<span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">Flow</span>
            </Link>
            <p className="text-slate-500 text-sm leading-relaxed">
              Turn one piece of content into platform-ready<br className="md:hidden" />{' '}posts for every channel.
            </p>
          </div>

          {/* Column 2: Legal */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Legal</h4>
            <nav className="flex flex-col gap-2">
              {legalLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="text-slate-500 text-sm hover:text-indigo-600 transition-colors duration-200"
                  data-testid={`footer-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Column 3: Contact */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Contact</h4>
            <div className="space-y-2">
              <a 
                href="mailto:info@brandcontentflow.com" 
                className="flex items-center gap-2 text-slate-500 text-sm hover:text-indigo-600 transition-colors"
              >
                <Mail className="w-4 h-4 shrink-0" />
                info@brandcontentflow.com
              </a>
              <a 
                href="tel:+442077194000" 
                className="flex items-center gap-2 text-slate-500 text-sm hover:text-indigo-600 transition-colors"
              >
                <Phone className="w-4 h-4 shrink-0" />
                +44 20 7719 4000
              </a>
              <div className="flex items-start gap-2 text-slate-500 text-sm">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                <span>1 Canada Square, Canary Wharf,<br className="md:hidden" />{' '}London E14 5AB, UK</span>
              </div>
            </div>
          </div>

          {/* Column 4: Follow Us */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-3">Follow Us</h4>
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                social.href.startsWith('mailto') ? (
                  <a
                    key={social.label}
                    href={social.href}
                    className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-all duration-200"
                    aria-label={social.label}
                    data-testid={`social-link-${social.label.toLowerCase()}`}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ) : (
                  <Link
                    key={social.label}
                    to={social.href}
                    className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 transition-all duration-200"
                    aria-label={social.label}
                    data-testid={`social-link-${social.label.toLowerCase()}`}
                  >
                    <social.icon className="w-4 h-4" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-slate-200 text-center">
          <p className="text-slate-400 text-sm">
            © {currentYear} BrandContentFlow. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
